import React, { useState } from 'react';
import { Empty, ErrorText, Panel, useChrome } from './ceoChrome';

const ACTIONS = [
  { id: 'extend_trial', label: 'Extend trial', fields: ['days'] },
  { id: 'grant_free_months', label: 'Grant free months', fields: ['months'] },
  { id: 'change_plan', label: 'Change plan', fields: ['plan', 'seats'] },
  { id: 'pause', label: 'Pause', fields: [] },
  { id: 'reactivate', label: 'Reactivate', fields: [] },
  { id: 'resend_invites', label: 'Resend invites', fields: [] },
];

const LIVE_ACTIONS = {
  extend_trial: true,
  grant_free_months: true,
  change_plan: true,
  pause: true,
  reactivate: true,
};

function liveSentence(action, pending, agent) {
  const name = agent && agent.name ? agent.name : 'this agent';
  const days = Number(pending && pending.days) || 0;
  const months = Number(pending && pending.months) || 0;
  if (action === 'extend_trial') {
    return `Extend ${name}'s trial by ${days} ${days === 1 ? 'day' : 'days'}. The trial stays at $0.`;
  }
  if (action === 'grant_free_months') {
    return `Give ${name} ${months} free ${months === 1 ? 'month' : 'months'} on the live account.`;
  }
  if (action === 'change_plan') {
    if (pending && pending.plan === 'team') return `Change ${name} to a team plan with ${pending.seats} seats.`;
    return `Change ${name} to the solo plan.`;
  }
  if (action === 'pause') return `Pause collection for ${name}.`;
  if (action === 'reactivate') return `Resume collection for ${name}.`;
  return `Update ${name} on the live Stripe account.`;
}

export default function AccountControlsPanel({ colors, narrow, data, error, onAction, busy }) {
  const chrome = useChrome(colors, narrow);
  const agents = (data && data.agents) || [];
  const [profileId, setProfileId] = useState('');
  const [action, setAction] = useState('extend_trial');
  const [days, setDays] = useState('7');
  const [months, setMonths] = useState('1');
  const [plan, setPlan] = useState('solo');
  const [seats, setSeats] = useState('2');
  const [pending, setPending] = useState(null);
  const [notice, setNotice] = useState('');
  const [localError, setLocalError] = useState('');
  const chosen = ACTIONS.find((item) => item.id === action) || ACTIONS[0];
  const stripeMode = data && data.stripeMode;
  const liveMode = stripeMode === 'live';
  const agent = agents.find((item) => item.id === profileId);
  const liveAction = Boolean(pending && liveMode && LIVE_ACTIONS[pending.action]);

  async function confirm() {
    if (!onAction || !pending) return;
    setLocalError('');
    try {
      const result = await onAction({ ...pending, confirm: true });
      setPending(null);
      if (result && result.skipped != null) {
        setNotice(`Sent to ${result.sent} test inbox${result.sent === 1 ? '' : 'es'}. Skipped ${result.skipped}.`);
      } else if (result && result.stripe === 'live') {
        setNotice('Saved on the live Stripe account.');
      } else if (result && result.stripe === 'test') {
        setNotice('Saved in Stripe test mode.');
      } else {
        setNotice('Saved.');
      }
    } catch (err) {
      setLocalError(err.message || 'Could not update the account');
    }
  }

  const intro = liveMode
    ? 'These actions change the live Stripe account. Each one asks before it runs, and each one is logged. Invites still go only to Resend test inboxes.'
    : 'Trial extensions, test-mode credits, plan changes, pause, and invite resends. Each one asks before it runs, and each one is logged. Invites go only to Resend test inboxes.';

  return (
    <div style={chrome.page} data-testid="ceo-accounts">
      <div>
        <h2 style={chrome.title}>Account controls</h2>
        <p style={chrome.sub}>{intro}</p>
      </div>
      {error || localError ? <ErrorText>{error || localError}</ErrorText> : null}
      {notice ? <p style={{ margin: 0 }}>{notice}</p> : null}
      {!data && !error ? <Empty chrome={chrome}>Loading…</Empty> : null}
      <Panel chrome={chrome} title="Control">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select aria-label="Agent" value={profileId} onChange={(event) => setProfileId(event.target.value)} style={chrome.field}>
            <option value="">Choose an agent</option>
            {agents.map((row) => (
              <option key={row.id} value={row.id}>{row.name} · {row.email}</option>
            ))}
          </select>
          <select aria-label="Action" value={action} onChange={(event) => { setAction(event.target.value); setPending(null); }} style={chrome.field}>
            {ACTIONS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
          {chosen.fields.includes('days') ? <input aria-label="Days" inputMode="numeric" value={days} onChange={(event) => setDays(event.target.value)} style={chrome.field} /> : null}
          {chosen.fields.includes('months') ? <input aria-label="Months" inputMode="numeric" value={months} onChange={(event) => setMonths(event.target.value)} style={chrome.field} /> : null}
          {chosen.fields.includes('plan') ? (
            <select aria-label="Plan" value={plan} onChange={(event) => setPlan(event.target.value)} style={chrome.field}>
              <option value="solo">Solo</option>
              <option value="team">Team</option>
            </select>
          ) : null}
          {chosen.fields.includes('seats') && plan === 'team' ? (
            <select aria-label="Seats" value={seats} onChange={(event) => setSeats(event.target.value)} style={chrome.field}>
              {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => <option key={value} value={value}>{value} seats</option>)}
            </select>
          ) : null}
          {pending && !liveAction ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" style={chrome.button} disabled={busy} onClick={confirm}>Save {chosen.label.toLowerCase()}</button>
              <button type="button" style={chrome.quiet} onClick={() => setPending(null)}>Cancel</button>
            </div>
          ) : (
            <button
              type="button"
              style={chrome.button}
              disabled={!profileId}
              onClick={() => setPending({
                action,
                profileId,
                days: Number(days),
                months: Number(months),
                plan,
                seats: Number(seats),
              })}
            >
              {chosen.label}
            </button>
          )}
        </div>
      </Panel>
      {liveAction ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 20,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="live-confirm-title"
            style={{
              width: '100%',
              maxWidth: 420,
              background: chrome.surface,
              color: chrome.text,
              borderRadius: 16,
              padding: narrow ? 16 : 24,
              boxSizing: 'border-box',
              fontFamily: chrome.page.fontFamily,
            }}
          >
            <h3 id="live-confirm-title" style={{ ...chrome.title, fontSize: 22 }}>Save live change</h3>
            <p style={chrome.sub}>This changes the live Stripe account. {liveSentence(pending.action, pending, agent)} This is written to the audit log.</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16, flexDirection: narrow ? 'column' : 'row' }}>
              <button type="button" style={chrome.button} disabled={busy} onClick={confirm}>Save</button>
              <button type="button" style={chrome.quiet} onClick={() => setPending(null)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
